import { ArrowDownTrayIcon, EnvelopeIcon, TrashIcon } from '@heroicons/react/24/outline'
import { getBackgroundRuns, onBackgroundRunsChanged, trackStatementRun } from 'components/BackgroundRuns'
import { DataGrid, GridColumn, GridSort } from 'components/DataGrid'
import { Frequency } from 'components/Frequency'
import { RepeatType, WeekOrdOfMonths } from 'components/Frequency/constants'
import { validateFrequencyData } from 'components/Frequency/logic'
import { HoverTip } from 'components/HoverTip'
import { LayoutLoading } from 'components/LayoutLoading'
import { LoanAttributeExclusions } from 'components/LoanAttributeExclusions'
import { STATEMENT_TYPE_OPTIONS, StatementTypeScope } from 'components/StatementTypeScope'
import { TagMatch } from 'components/TagMatchToggle'
import { TagScopePicker } from 'components/TagScopePicker'
import { daysOfWeek } from 'config'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  type StatementScheduleData,
  cancelStatementGeneration,
  createStatementSchedule,
  deleteServicingStatement,
  deleteStatementBatch,
  deleteStatementSchedule,
  downloadServicingStatement,
  downloadStatementBatch,
  emailStatementBatch,
  generateServicingStatements,
  getBatchStatements,
  getLoanTagSuggestions,
  getStatementBatches,
  getStatementGenerationStatus,
  getStatementSchedules,
  printLoanStatement,
  runStatementScheduleById,
  toastUnlessApiShown,
  updateStatementSchedule,
} from 'services'
import { Button, Checkbox, Input2, Modal } from 'stories/components'

interface Batch {
  id: number
  batchDate?: string
  statementDate?: string
  periodStart?: string
  periodEnd?: string
  count?: number
  status?: string
  emailedAt?: string
}

interface BatchStatement {
  id: number
  loanNumber: number
  borrowerName?: string
  amountDue?: number
  emailedAt?: string
  emailedTo?: string
}

const money = (v?: number) =>
  v == null ? '' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const dl = (blob: Blob, name: string) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  URL.revokeObjectURL(url)
}

export default function ServicingStatements() {
  const [loading, setLoading] = useState(true)
  const [batches, setBatches] = useState<Batch[]>([])
  const [showGenerate, setShowGenerate] = useState(false)
  const [showSchedule, setShowSchedule] = useState(false)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [contents, setContents] = useState<Record<number, BatchStatement[]>>({})
  const [sort, setSort] = useState<GridSort | null>(null)
  // The window of statement dates the list is narrowed to. Both blank by default, so the page opens
  // on the whole history the way it always has.
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  const load = () => {
    setLoading(true)
    getStatementBatches()
      .then((res: any) => res?.success && setBatches(res.data || []))
      .finally(() => setLoading(false))
  }
  useEffect(() => {
    load()
  }, [])

  // A statement run handed to the background finishes with this page already open and a batch list
  // that does not know about the batch it just produced. Reload once per finished run, tracked by
  // job id so a finished run sitting in the tray does not reload on every unrelated tray change.
  const reloadedFor = useRef<Set<string>>(new Set())
  useEffect(
    () =>
      onBackgroundRunsChanged(() => {
        const landed = getBackgroundRuns().filter(
          (run) => run.kind === 'statement' && run.status !== 'running' && !reloadedFor.current.has(run.jobId),
        )
        if (!landed.length) return
        landed.forEach((run) => reloadedFor.current.add(run.jobId))
        load()
      }),
    [],
  )

  const toggle = async (batch: Batch) => {
    if (expanded === batch.id) {
      setExpanded(null)
      return
    }
    setExpanded(batch.id)
    if (!contents[batch.id]) {
      const res: any = await getBatchStatements(batch.id)
      if (res?.success) setContents((c) => ({ ...c, [batch.id]: res.data || [] }))
    }
  }

  const onDownloadBatch = async (batch: Batch) => {
    const blob: Blob = await downloadStatementBatch({ id: batch.id })
    dl(blob, `statement-batch-${batch.id}-${batch.statementDate || batch.batchDate}.pdf`)
  }

  const onEmailBatch = async (batch: Batch) => {
    if (!window.confirm(`Email all ${batch.count} statement(s) in batch #${batch.id} to borrowers?`)) return
    setLoading(true)
    const res: any = await emailStatementBatch({ id: batch.id })
    setLoading(false)
    if (res?.success) {
      toastUnlessApiShown(res, 'Statements emailed', 'success')
      load()
    } else {
      toastUnlessApiShown(res, 'Statements were not emailed.', 'error')
    }
  }

  const onDownloadOne = async (s: BatchStatement) => {
    const blob: Blob = await downloadServicingStatement({ id: s.id })
    dl(blob, `statement-${s.loanNumber}-${s.id}.pdf`)
  }

  /**
   * Withdraw a whole run. This is the mistake that actually happens - a wrong period or a wrong
   * loan set - and it takes every statement in it off its loan's documents and off the borrower
   * portal. Statements already emailed stay sent, so the confirm says how many.
   */
  const onDeleteBatch = async (batch: Batch) => {
    const sent = batch.emailedAt
      ? '\n\nThis batch has already been emailed to borrowers. Deleting it cannot unsend those emails.'
      : ''
    if (
      !window.confirm(
        `Delete batch #${batch.id} and all ${
          batch.count ?? 0
        } statement(s) in it?\n\nEach one is removed from its loan's statements, from the loan documents and from the borrower portal. This cannot be undone.${sent}`,
      )
    )
      return
    setLoading(true)
    const res: any = await deleteStatementBatch(batch.id)
    setLoading(false)
    toastUnlessApiShown(
      res,
      res?.success ? 'Batch deleted.' : 'Batch was not deleted.',
      res?.success ? 'success' : 'error',
    )
    if (res?.success) {
      setExpanded(null)
      setContents((c) => {
        const next = { ...c }
        delete next[batch.id]
        return next
      })
      load()
    }
  }

  // One statement out of a run. The batch's own count moves with it, so the list is reloaded too.
  const onDeleteOne = async (batch: Batch, s: BatchStatement) => {
    const sent = s.emailedAt
      ? `\n\nIt was emailed to ${s.emailedTo || 'the borrower'}. Deleting it cannot unsend that email.`
      : ''
    if (
      !window.confirm(
        `Delete loan ${s.loanNumber}'s statement from batch #${batch.id}?\n\nIt will be removed from the loan's statements, from the loan documents and from the borrower portal.${sent}`,
      )
    )
      return
    setLoading(true)
    const res: any = await deleteServicingStatement(s.id)
    setLoading(false)
    toastUnlessApiShown(
      res,
      res?.success ? 'Statement deleted.' : 'Statement was not deleted.',
      res?.success ? 'success' : 'error',
    )
    if (!res?.success) return
    const refreshed: any = await getBatchStatements(batch.id)
    setContents((c) => ({ ...c, [batch.id]: refreshed?.success ? refreshed.data || [] : [] }))
    load()
  }

  const columns = useMemo<GridColumn<Batch>[]>(
    () => [
      { key: 'id', label: 'Batch #', sortable: true, hideable: false, defaultWidth: 90 },
      { key: 'batchDate', label: 'Generated', sortable: true, defaultWidth: 130 },
      { key: 'statementDate', label: 'Statement Date', sortable: true, defaultWidth: 140 },
      {
        key: 'period',
        label: 'Period',
        defaultWidth: 180,
        render: (b) => `${b.periodStart ?? ''} – ${b.periodEnd ?? ''}`,
      },
      { key: 'count', label: 'Statements', sortable: true, align: 'right', defaultWidth: 110 },
      {
        key: 'emailedAt',
        label: 'Emailed',
        sortable: true,
        defaultWidth: 100,
        render: (b) =>
          b.emailedAt ? (
            <span className="text-green-700 text-xs">Sent</span>
          ) : (
            <span className="text-gray-400 text-xs">—</span>
          ),
      },
      {
        key: 'actions',
        label: 'Actions',
        hideable: false,
        reorderable: false,
        defaultWidth: 150,
        render: (b) => (
          <div className="flex items-center gap-3.5 text-slate-500">
            <HoverTip message="Download the combined PDF for this batch">
              <button
                aria-label="Download batch PDF"
                className="hover:text-shade-blue"
                onClick={() => onDownloadBatch(b)}
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
              </button>
            </HoverTip>
            <HoverTip message="Email each statement in this batch to its borrower">
              <button aria-label="Email statements" className="hover:text-shade-blue" onClick={() => onEmailBatch(b)}>
                <EnvelopeIcon className="h-4 w-4" />
              </button>
            </HoverTip>
            <HoverTip message="Delete this batch and every statement in it, including the loan-document and portal copies">
              <button aria-label="Delete batch" className="hover:text-red-600" onClick={() => onDeleteBatch(b)}>
                <TrashIcon className="h-4 w-4" />
              </button>
            </HoverTip>
          </div>
        ),
      },
    ],
    [],
  )

  // A batch is placed by the date it states, falling back to the day it was generated - the same
  // pair the batch PDF is named after. One carrying neither stays in the list however it is
  // narrowed, so a window can hide nothing.
  const batchDateOf = (b: Batch) => b.statementDate || b.batchDate || ''

  const filteredBatches = useMemo(() => {
    if (!fromDate && !toDate) return batches
    return batches.filter((b) => {
      const d = batchDateOf(b)
      if (!d) return true
      if (fromDate && d < fromDate) return false
      if (toDate && d > toDate) return false
      return true
    })
  }, [batches, fromDate, toDate])

  const sortedBatches = useMemo(() => {
    if (!sort) return filteredBatches
    const { key, dir } = sort
    const sorted = [...filteredBatches].sort((a, b) => {
      const av = (a as any)[key]
      const bv = (b as any)[key]
      let cmp: number
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv
      else cmp = String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true })
      return dir === 'asc' ? cmp : -cmp
    })
    return sorted
  }, [filteredBatches, sort])

  const renderBatchContents = (b: Batch) => (
    <>
      <div className="text-xs font-semibold text-gray-500 mb-2">Printed in this batch</div>
      <table className="min-w-full text-sm">
        <thead className="text-gray-500">
          <tr>
            <th className="px-2 py-1 text-left">Loan #</th>
            <th className="px-2 py-1 text-left">Borrower</th>
            <th className="px-2 py-1 text-left">Amount Due</th>
            <th className="px-2 py-1 text-left">Emailed</th>
            <th className="px-2 py-1 text-left"></th>
          </tr>
        </thead>
        <tbody>
          {(contents[b.id] || []).map((s) => (
            <tr key={s.id} className="border-t border-gray-100">
              <td className="px-2 py-1">
                <Link className="text-shade-blue hover:underline" to={`/servicing/${s.loanNumber}?menu=loanPayments`}>
                  {s.loanNumber}
                </Link>
              </td>
              <td className="px-2 py-1">{s.borrowerName}</td>
              <td className="px-2 py-1">{money(s.amountDue)}</td>
              <td className="px-2 py-1 text-xs">
                {s.emailedAt ? (
                  <span className="text-green-700">{s.emailedTo || 'Sent'}</span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className="px-2 py-1">
                <div className="flex items-center gap-3">
                  <button className="text-shade-blue hover:underline" onClick={() => onDownloadOne(s)}>
                    Download
                  </button>
                  <button className="text-red-600 hover:underline" onClick={() => onDeleteOne(b, s)}>
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {(contents[b.id] || []).length === 0 && (
            <tr>
              <td colSpan={5} className="px-2 py-3 text-gray-400">
                Loading…
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  )

  return (
    <div className="px-6 py-4">
      <LayoutLoading show={loading} />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold">Statements</h1>
          <p className="text-sm text-gray-500">
            {filteredBatches.length === batches.length
              ? `${batches.length} batches`
              : `${filteredBatches.length} of ${batches.length} batches`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button color="gray" outline onClick={() => setShowSchedule(true)}>
            Schedule
          </Button>
          <Button color="blue" onClick={() => setShowGenerate(true)}>
            Generate Statements
          </Button>
        </div>
      </div>

      {/* Statement-date window, the same control the ACH screen carries. Leaving both blank lists
          every batch, which is how the page opened before there was a filter. */}
      <div className="sticky top-0 z-20 -mx-6 mb-3 flex flex-wrap items-center gap-3 border-b border-gray-200 bg-white px-6 py-2">
        <div className="ml-auto flex items-center gap-2 text-sm text-gray-600">
          <span>Statement dates</span>
          <input
            type="date"
            aria-label="Earliest statement date"
            title="Earliest statement date to include. Leave blank for everything up to the end date."
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <span>to</span>
          <input
            type="date"
            aria-label="Latest statement date"
            title="Latest statement date to include. Leave blank for everything from the start date on."
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
          {(fromDate || toDate) && (
            <button
              className="text-xs text-shade-blue hover:underline"
              onClick={() => {
                setFromDate('')
                setToDate('')
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <DataGrid<Batch>
        gridKey="servicing-statements"
        columns={columns}
        rows={sortedBatches}
        rowKey={(b) => b.id}
        sort={sort}
        onSortChange={setSort}
        loading={loading}
        emptyText={
          batches.length
            ? 'No statement batches in this date range.'
            : 'No statement batches yet. Use “Generate Statements” to create one.'
        }
        expandable
        isRowExpanded={(b) => expanded === b.id}
        onToggleExpand={toggle}
        renderExpanded={renderBatchContents}
      />

      {showGenerate && (
        <GenerateModal
          onClose={() => setShowGenerate(false)}
          onDone={() => {
            setShowGenerate(false)
            setContents({})
            load()
          }}
        />
      )}

      {showSchedule && (
        <ScheduleModal
          onClose={() => setShowSchedule(false)}
          onRan={() => {
            setShowSchedule(false)
            setContents({})
            load()
          }}
        />
      )}
    </div>
  )
}

// Configure a recurring statement-generation schedule.
/**
 * How a schedule's repeat reads in the list, e.g. "Every month on day 20 at 08:00".
 *
 * ⚠️ The day of the month is NOT a top-level frequency field: `Frequency` nests it under
 * `custom.dateOfMonth`, with `custom.monthType` choosing between a date ("the 20th") and an
 * ordinal weekday ("the third Friday"). Reading `frequency.day` silently drops the one part of
 * the line a servicer checks, so the three schedules all read alike.
 */
const scheduleSummary = (s: StatementScheduleData) => {
  const f: Record<string, any> = s.frequency || {}
  if (f.repeatType !== RepeatType.Repeat) return 'Not repeating'
  const every = Number(f.repeatEvery || 1)
  const unit = String(f.intervalUnit || 'month')
  const custom: Record<string, any> = f.custom || {}
  let when = ''
  if (unit === 'week') {
    const days = (f.weekDays || []).map((v: any) => daysOfWeek[v]).filter(Boolean)
    when = days.length ? ` on ${days.join(', ')}` : ''
  } else if (unit === 'month') {
    if (custom.monthType === 'date' && custom.dateOfMonth) when = ` on day ${custom.dateOfMonth}`
    else if (custom.monthType === 'day' && custom.weekOrdOfMonth)
      when = ` on the ${(WeekOrdOfMonths[custom.weekOrdOfMonth] || '').toLowerCase()} ${
        daysOfWeek[Number(custom.weekDayOfMonth)] || ''
      }`.trimEnd()
  }
  const time =
    f.hour === null || f.hour === undefined || f.minute === null || f.minute === undefined || f.hour === ''
      ? ''
      : ` at ${String(f.hour).padStart(2, '0')}:${String(f.minute).padStart(2, '0')}`
  return `Every ${every > 1 ? `${every} ${unit}s` : unit}${when}${time}`
}

/** The type scope as text. Empty is every type, which is the trap worth spelling out in the list. */
const scheduleTypes = (s: StatementScheduleData) => {
  const types = s.statementTypes || []
  if (!types.length) return 'All types'
  return types.map((t) => STATEMENT_TYPE_OPTIONS.find((o) => o.value === t)?.label || t).join(', ')
}

/**
 * The saved statement runs.
 *
 * ⚠️ This used to be a single-schedule dialog, because the backend read the table as a singleton:
 * one statement date a month for the whole book. A servicer billing payment statements on the 20th,
 * construction on the 25th and lines on the 30th needs one row each, so this is a list.
 */
function ScheduleModal({ onClose, onRan }: { onClose: () => void; onRan: () => void }) {
  const [loading, setLoading] = useState(true)
  const [schedules, setSchedules] = useState<StatementScheduleData[]>([])
  const [editing, setEditing] = useState<StatementScheduleData | null>(null)
  const [runningId, setRunningId] = useState(0)

  const reload = () => {
    setLoading(true)
    getStatementSchedules()
      .then((res: any) => setSchedules(Array.isArray(res?.data) ? res.data : []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(reload, [])

  const onRunNow = async (s: StatementScheduleData) => {
    setRunningId(Number(s.id))
    try {
      const res: any = await runStatementScheduleById(Number(s.id))
      toastUnlessApiShown(res, 'Statement run started.', res?.success === 0 ? 'error' : 'success')
      if (res?.success !== 0) onRan()
    } catch {
      toast('Failed to run statements.', { type: 'error' })
    } finally {
      setRunningId(0)
    }
  }

  const onDelete = async (s: StatementScheduleData) => {
    // Only the schedule goes. Statements it already generated are removed on the Statements
    // screens, so this cannot quietly unpublish anything a borrower has.
    if (
      !window.confirm(`Remove the "${s.name || 'Untitled'}" statement schedule? Statements already generated are kept.`)
    )
      return
    try {
      const res: any = await deleteStatementSchedule(Number(s.id))
      toastUnlessApiShown(res, 'Statement schedule removed.', res?.success === 0 ? 'error' : 'success')
      reload()
    } catch {
      toast('Failed to remove the schedule.', { type: 'error' })
    }
  }

  return (
    // The editor is a SIBLING of the list modal, not a child of it: `Modal` takes a single
    // `JSX.Element | string` child, so a second one does not typecheck.
    <>
      <Modal
        title="Schedule Statements"
        isOpen
        onClose={onClose}
        onOk={onClose}
        titleOkay="Close"
        titleCancel=""
        loading={loading}
      >
        <div className="w-[min(720px,calc(100vw-25rem))] min-h-[320px]">
          <p className="mb-3 text-sm text-gray-500">
            Each schedule generates statements for the <b>previous month</b> (1st to end), dated the{' '}
            <b>1st of the following month</b>. Give each statement type its own schedule to bill them on different days.
          </p>

          {!loading && !schedules.length && (
            <div className="rounded border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
              No statement schedules yet. Add one to generate statements automatically.
            </div>
          )}

          {schedules.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-400">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Types</th>
                  <th className="py-2 pr-4">Repeat</th>
                  <th className="py-2 pr-4">Last run</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {schedules.map((s) => (
                  <tr key={s.id} className={`border-b border-gray-100 ${s.enabled ? '' : 'opacity-50'}`}>
                    <td className="py-2 pr-4">
                      {s.name || <span className="text-gray-400">Untitled</span>}
                      {!s.enabled && <span className="ml-2 text-xs text-gray-400">(disabled)</span>}
                    </td>
                    <td className="py-2 pr-4">{scheduleTypes(s)}</td>
                    <td className="py-2 pr-4 text-gray-600">{scheduleSummary(s)}</td>
                    <td className="py-2 pr-4 text-gray-600">
                      {s.lastRunDate || <span className="text-gray-400">Never</span>}
                    </td>
                    <td className="py-2">
                      <div className="flex items-center justify-end gap-3 whitespace-nowrap text-xs">
                        <button type="button" className="text-shade-blue hover:underline" onClick={() => setEditing(s)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="text-shade-blue hover:underline disabled:opacity-40"
                          disabled={!!runningId}
                          onClick={() => onRunNow(s)}
                        >
                          {runningId === s.id ? 'Running…' : 'Run now'}
                        </button>
                        <button type="button" className="text-red-600 hover:underline" onClick={() => onDelete(s)}>
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-3">
            <span className="text-[12px] text-gray-400">
              Scheduled runs only fire in production. Use “Run now” to generate immediately.
            </span>
            <Button color="gray" outline onClick={() => setEditing({} as StatementScheduleData)}>
              Add schedule
            </Button>
          </div>
        </div>
      </Modal>

      {editing && (
        <ScheduleEditor
          schedule={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            reload()
          }}
        />
      )}
    </>
  )
}

/** One schedule: its name, its type scope, when it repeats, and the loans it selects. */
function ScheduleEditor({
  schedule,
  onClose,
  onSaved,
}: {
  schedule: StatementScheduleData
  onClose: () => void
  onSaved: () => void
}) {
  const isNew = !schedule.id
  const [saving, setSaving] = useState(false)
  const [errorCheck, setErrorCheck] = useState(false)
  const [name, setName] = useState(schedule.name || '')
  // Seed a recurring type so the time fields show and a new schedule is valid.
  const [frequency, setFrequency] = useState<Record<string, any>>(
    schedule.frequency && Object.keys(schedule.frequency).length
      ? schedule.frequency
      : { repeatType: RepeatType.Repeat },
  )
  const [enabled, setEnabled] = useState(schedule.enabled ?? true)
  const [emailNow, setEmailNow] = useState(schedule.id ? !!schedule.emailNow : true)
  const [portalVisible, setPortalVisible] = useState(schedule.portalVisible ?? true)
  const [statementTypes, setStatementTypes] = useState<string[]>(schedule.statementTypes || [])
  // The loan-selection rule the schedule runs with. Shown here, and not only in the generate
  // dialog, because a rule nobody can see is a rule nobody maintains: typed into a manual run it
  // survived one batch, and the next scheduled run quietly statemented every loan it excluded.
  const [allTags, setAllTags] = useState<string[]>([])
  const [tags, setTags] = useState<string[]>(schedule.tags || [])
  const [tagMatch, setTagMatch] = useState<TagMatch>((schedule.tagMatch as TagMatch) || 'any')
  const [excludeTags, setExcludeTags] = useState<string[]>(schedule.excludeTags || [])
  const [excludeInterestTypes, setExcludeInterestTypes] = useState<string[]>(schedule.excludeInterestTypes || [])

  useEffect(() => {
    getLoanTagSuggestions('')
      .then((list) => setAllTags(Array.isArray(list) ? list : []))
      .catch(() => {})
  }, [])

  const onSave = async () => {
    setErrorCheck(true)
    if (enabled && validateFrequencyData(frequency, [], true)) {
      toast('Please complete the schedule.', { type: 'error' })
      return
    }
    setSaving(true)
    try {
      // Always stamp the repeat type from the Enabled toggle so the runner recognizes it.
      const freq = { ...frequency, repeatType: enabled ? RepeatType.Repeat : RepeatType.NoRepeat }
      const payload: StatementScheduleData = {
        name,
        frequency: freq,
        emailNow,
        portalVisible,
        enabled,
        tags,
        tagMatch,
        excludeTags,
        excludeInterestTypes,
        statementTypes,
      }
      const res: any = isNew
        ? await createStatementSchedule(payload)
        : await updateStatementSchedule(Number(schedule.id), payload)
      if (res?.success === 0) {
        toastUnlessApiShown(res, 'Failed to save the schedule.', 'error')
        return
      }
      toastUnlessApiShown(res, 'Statement schedule saved.', 'success')
      onSaved()
    } catch {
      toast('Failed to save the schedule.', { type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={isNew ? 'Add Statement Schedule' : 'Edit Statement Schedule'}
      isOpen
      onOk={onSave}
      onClose={onClose}
      loading={saving}
      // Opens over the schedule list, so it needs the stacked tier or the list's backdrop covers it.
      childLevel={1}
    >
      <div className="w-[min(560px,calc(100vw-25rem))] min-h-[380px]">
        <Input2 title="Name" value={name} onChange={setName} className="mb-3" placeholder="e.g. Payment statements" />
        <StatementTypeScope
          className="mb-4"
          statementTypes={statementTypes}
          onChangeStatementTypes={setStatementTypes}
        />
        <div className="mb-3 border-t border-slate-100 pt-3">
          <Checkbox id="stmt-sched-enabled" title="Enabled" value={enabled} onChange={setEnabled} />
        </div>
        <div className={enabled ? '' : 'pointer-events-none opacity-40'}>
          <Frequency
            data={frequency}
            onChange={(value: any) => setFrequency(value)}
            errorCheck={errorCheck}
            repeatTypeToggle={false}
            showTime={true}
          />
        </div>
        <div className="mt-4 flex flex-col gap-2">
          <Checkbox
            id="stmt-sched-email"
            title="Email each borrower their statement"
            value={emailNow}
            onChange={setEmailNow}
          />
          <Checkbox
            id="stmt-sched-portal"
            title="Make statements available on the portal"
            value={portalVisible}
            onChange={setPortalVisible}
          />
        </div>

        <TagScopePicker
          className="mt-4 border-t border-slate-100 pt-3"
          allTags={allTags}
          tags={tags}
          onChangeTags={setTags}
          tagMatch={tagMatch}
          onChangeTagMatch={setTagMatch}
          excludeTags={excludeTags}
          onChangeExcludeTags={setExcludeTags}
          includeHint={`(optional - only loans with ${
            tags.length > 1 && tagMatch === 'all' ? 'every' : 'a'
          } selected tag get a statement)`}
        />
        <LoanAttributeExclusions
          className="mt-3"
          interestTypes={excludeInterestTypes}
          onChangeInterestTypes={setExcludeInterestTypes}
        />
      </div>
    </Modal>
  )
}

// The month before the statement date, 1st to end: the same period a scheduled run bills.
const previousMonth = () => {
  const now = new Date()
  const ymd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return {
    start: ymd(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
    end: ymd(new Date(now.getFullYear(), now.getMonth(), 0)),
  }
}

/**
 * Open a freshly printed statement in the tab reserved for it, or download it when the browser
 * blocked that tab. The tab is opened on the click itself, before the statement is built, because
 * a window opened after an await is treated as a popup.
 */
const showStatementPdf = (blob: Blob, name: string, win: Window | null) => {
  const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
  if (win && !win.closed) win.location.href = url
  else dl(blob, name)
  // The open tab still needs the URL after this returns; free it once it has had time to load.
  setTimeout(() => URL.revokeObjectURL(url), 60000)
}

// Batch-generate statements for a period, optionally for a subset of loans, with an
// option to email them to borrowers immediately.
//
// Given a `loanNumber` it prints that one loan's statement instead, with the same options: the
// statement is stored on the loan (its Statements screen and documents) and opened to print.
export function GenerateModal({
  onClose,
  onDone,
  loanNumber,
}: {
  onClose: () => void
  onDone: () => void
  loanNumber?: number
}) {
  const today = new Date().toISOString().slice(0, 10)
  const forLoan = !!loanNumber
  const [periodStart, setPeriodStart] = useState(() => (forLoan ? previousMonth().start : ''))
  const [periodEnd, setPeriodEnd] = useState(() => (forLoan ? previousMonth().end : ''))
  const [statementDate, setStatementDate] = useState(today)
  const [loanNumbers, setLoanNumbers] = useState('')
  const [emailNow, setEmailNow] = useState(false)
  const [portalVisible, setPortalVisible] = useState(true)
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState<{ handled: number; total: number } | null>(null)
  // The run in flight. Held so the progress panel can call it off or hand it to the tray.
  const [jobId, setJobId] = useState('')
  // A ref, not state: the poll closure that reads it is created once and must see the live value.
  const handedOff = useRef(false)
  const [queuedBehind, setQueuedBehind] = useState(-1)
  const [allTags, setAllTags] = useState<string[]>([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [tagMatch, setTagMatch] = useState<TagMatch>('any')
  const [excludedTags, setExcludedTags] = useState<string[]>([])
  const [excludeInterestTypes, setExcludeInterestTypes] = useState<string[]>([])
  const [statementTypes, setStatementTypes] = useState<string[]>([])
  // Whether the selection below arrived from a saved schedule, so the dialog can say so.
  const [fromSchedule, setFromSchedule] = useState('')
  // The saved schedules, to prefill this run's rule from one of them.
  const [schedules, setSchedules] = useState<StatementScheduleData[]>([])

  useEffect(() => {
    if (forLoan) return
    getLoanTagSuggestions('')
      .then((tags) => setAllTags(Array.isArray(tags) ? tags : []))
      .catch(() => {})
    // Start a manual run from a saved rule, so "which loans get a statement" is answered the same
    // way however the run was started. Without this the dialog opened empty every time and the rule
    // survived only in the operator's memory: longhorn ran a filtered batch of 1,479 and an
    // unfiltered one of 1,586 on the same morning with the same period, and the 107-loan difference
    // read as a bug for a month. Every field stays editable, so a deliberate one-off run is still
    // one Clear away.
    getStatementSchedules()
      .then((res: any) => {
        const list: StatementScheduleData[] = Array.isArray(res?.data) ? res.data : []
        setSchedules(list)
        // ⚠️ Only auto-apply when there is exactly ONE schedule. Once a tenant bills each type on
        // its own date, "the" saved rule no longer exists: seeding the first one would quietly
        // scope a manual run to Payment and look like the other types had stopped generating. With
        // several, the operator picks which rule to load.
        if (list.length === 1) applySchedule(list[0])
      })
      .catch(() => {})
  }, [])

  /** Load one schedule's rule into this run. Nothing here writes back to the schedule. */
  const applySchedule = (s: StatementScheduleData) => {
    // Whether borrowers are emailed comes from the schedule too: this dialog defaulted it off while
    // the schedule screen defaulted it on, so the same tenant got a different answer depending on
    // which screen the run was started from. Seeding it here leaves one place to state the intent.
    setEmailNow(!!s.emailNow)
    setSelectedTags(s.tags || [])
    setTagMatch((s.tagMatch as TagMatch) || 'any')
    setExcludedTags(s.excludeTags || [])
    setExcludeInterestTypes(s.excludeInterestTypes || [])
    setStatementTypes(s.statementTypes || [])
    setFromSchedule(
      [s.tags, s.excludeTags, s.excludeInterestTypes, s.statementTypes].some((list) => (list || []).length > 0)
        ? s.name || 'the saved schedule'
        : '',
    )
  }

  // One loan: build, store and open it. No job to poll; it is a single statement.
  const printOne = async () => {
    const win = window.open('', '_blank')
    setBusy(true)
    try {
      const res: any = await printLoanStatement(loanNumber as number, {
        periodStart,
        periodEnd,
        statementDate,
        emailNow,
        portalVisible,
      })
      if (!res?.success || !res.id) {
        win?.close()
        toast(res?.message || 'Could not print the statement.', { type: 'error' })
        return
      }
      const blob: Blob = await downloadServicingStatement({ id: res.id })
      showStatementPdf(blob, `statement-${loanNumber}-${statementDate}.pdf`, win)
      toast(res.message || 'Statement printed.', { type: res.message?.includes('Not emailed') ? 'warning' : 'success' })
      onDone()
    } catch {
      win?.close()
      toast('Could not print the statement.', { type: 'error' })
    } finally {
      setBusy(false)
    }
  }

  // Start the run in the background, then poll its status to drive the progress bar.
  const submit = async () => {
    if (!periodStart || !periodEnd) {
      toast('Choose a statement period', { type: 'error' })
      return
    }
    if (forLoan) {
      printOne()
      return
    }
    setBusy(true)
    setProgress({ handled: 0, total: 0 })
    const loans = loanNumbers
      .split(/[\s,]+/)
      .map((s) => Number(s.trim()))
      .filter((n) => !Number.isNaN(n) && n > 0)
    const start: any = await generateServicingStatements({
      periodStart,
      periodEnd,
      statementDate,
      loanNumbers: loans,
      emailNow,
      portalVisible,
      tags: selectedTags,
      tagMatch,
      excludeTags: excludedTags,
      excludeInterestTypes,
      statementTypes,
    })
    const startedJobId = start?.jobId
    if (!startedJobId) {
      setBusy(false)
      setProgress(null)
      toastUnlessApiShown(start, 'Could not start statement generation.', 'error')
      return
    }
    setJobId(startedJobId)
    const poll = async () => {
      // Handed to the tray, or called off: the tray polls from here on, or nobody needs to.
      if (handedOff.current) return
      const st: any = await getStatementGenerationStatus(startedJobId)
      if (!st || st.success === 0) {
        setBusy(false)
        setProgress(null)
        toastUnlessApiShown(st, 'Lost track of the run.', 'error')
        return
      }
      setQueuedBehind(st.queued ? st.queuedBehind ?? 0 : -1)
      setProgress({ handled: st.handled || 0, total: st.total || 0 })
      if (st.done) {
        setBusy(false)
        setProgress(null)
        setJobId('')
        if (st.error) {
          toast(st.error, { type: 'error' })
        } else {
          // A cancelled run reports what it did produce; the batch is real either way.
          toast(st.result?.message || `Generated batch of ${st.result?.count ?? ''} statement(s)`, {
            type: st.result?.success === 0 ? 'error' : st.result?.canceled ? 'warning' : 'success',
          })
          onDone()
        }
        return
      }
      setTimeout(poll, 500)
    }
    poll()
  }

  /**
   * Stop the run. The statements already written are kept, so this closes back to the batch list
   * rather than pretending nothing happened.
   */
  const cancelRun = async () => {
    if (!jobId) return
    handedOff.current = true
    setBusy(false)
    setProgress(null)
    setJobId('')
    await cancelStatementGeneration(jobId).catch(() => null)
    toast('Stopping the run. Statements already generated are kept.', { type: 'warning' })
    onDone()
  }

  /** Hand the run to the background tray and close. The run itself carries on regardless. */
  const runInBackground = () => {
    if (!jobId) return
    handedOff.current = true
    trackStatementRun(jobId, `Statement batch ${periodStart} to ${periodEnd}`)
    toast('Running in the background. You will be notified when it is ready.', { type: 'info' })
    onClose()
  }

  const pct = progress?.total ? Math.min(100, Math.round((progress.handled / progress.total) * 100)) : 0

  if (progress) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-lg shadow-xl w-[440px] max-w-[92vw] p-5">
          <h2 className="text-lg font-bold mb-1">Generating statements…</h2>
          <p className="text-sm text-gray-500 mb-4">
            {queuedBehind > 0
              ? `Waiting for ${queuedBehind} earlier run to finish. Portfolio runs go one at a time so they cannot slow each other, or the rest of the app, down. `
              : ''}
            Building statement PDFs{emailNow ? ' and emailing borrowers' : ''}. Leave this open, or send it to the
            background and carry on working.
          </p>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-shade-blue transition-all duration-300"
              style={{ width: progress.total ? `${pct}%` : '8%' }}
            />
          </div>
          <p className="mt-2 text-center text-sm text-gray-600">
            {progress.total
              ? `${progress.handled.toLocaleString()} of ${progress.total.toLocaleString()} (${pct}%)`
              : 'Starting…'}
          </p>
          <div className="mt-5 flex justify-end gap-2">
            <Button color="gray" outline onClick={cancelRun} disabled={!jobId}>
              Cancel Run
            </Button>
            <Button color="blue" onClick={runInBackground} disabled={!jobId}>
              Run in Background
            </Button>
          </div>
          <p className="mt-2 text-center text-[11px] text-gray-400">
            Cancelling stops between loans. Statements already generated are kept.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-[480px] max-w-[92vw] max-h-[90vh] flex flex-col p-5">
        <h2 className="shrink-0 text-lg font-bold mb-4">
          {forLoan ? `Print Statement - Loan ${loanNumber}` : 'Generate Statement Batch'}
        </h2>
        <div className="grid min-h-0 grid-cols-2 gap-3 overflow-y-auto pr-1 -mr-1">
          <label className="text-sm">
            Period Start
            <input
              type="date"
              className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
            />
          </label>
          <label className="text-sm">
            Period End
            <input
              type="date"
              className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
            />
          </label>
          <label className="text-sm col-span-2">
            Statement Date
            <input
              type="date"
              className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5"
              value={statementDate}
              onChange={(e) => setStatementDate(e.target.value)}
            />
          </label>
          {!forLoan && (
            <>
              <label className="text-sm col-span-2">
                Loan Numbers <span className="text-gray-400">(optional — blank = all active servicing loans)</span>
                <input
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5"
                  placeholder="e.g. 1001, 1002, 1005"
                  value={loanNumbers}
                  onChange={(e) => setLoanNumbers(e.target.value)}
                />
              </label>
              <TagScopePicker
                className="col-span-2"
                allTags={allTags}
                tags={selectedTags}
                onChangeTags={setSelectedTags}
                tagMatch={tagMatch}
                onChangeTagMatch={setTagMatch}
                excludeTags={excludedTags}
                onChangeExcludeTags={setExcludedTags}
                includeHint={`(optional — only loans with ${
                  selectedTags.length > 1 && tagMatch === 'all' ? 'every' : 'a'
                } selected tag get a statement)`}
              />
              <LoanAttributeExclusions
                className="col-span-2"
                interestTypes={excludeInterestTypes}
                onChangeInterestTypes={setExcludeInterestTypes}
              />
              <StatementTypeScope
                className="col-span-2"
                statementTypes={statementTypes}
                onChangeStatementTypes={setStatementTypes}
              />
              {schedules.length > 1 && (
                <label className="col-span-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  Prefill this run from a schedule:
                  <select
                    className="rounded border border-gray-300 px-2 py-1 text-xs"
                    value=""
                    onChange={(e) => {
                      const picked = schedules.find((s) => String(s.id) === e.target.value)
                      if (picked) applySchedule(picked)
                    }}
                  >
                    <option value="">Choose a schedule</option>
                    {schedules.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {s.name || `Schedule ${s.id}`}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {fromSchedule && (
                <p className="col-span-2 text-xs text-gray-500">
                  Prefilled from {fromSchedule}. Edit or clear it for a one-off run; changes here do not alter the
                  schedule.
                </p>
              )}
            </>
          )}
          {forLoan && (
            <p className="col-span-2 text-xs text-gray-500">
              A loan with a Billing Day is billed for its own cycle; this period applies when it has none.
            </p>
          )}
          <label className="flex items-center gap-2 text-sm col-span-2 mt-1">
            <input type="checkbox" checked={portalVisible} onChange={(e) => setPortalVisible(e.target.checked)} />
            {forLoan
              ? 'Make the statement available on the borrower portal'
              : 'Make statements available on the borrower portal'}
          </label>
          <label className="flex items-center gap-2 text-sm col-span-2">
            <input type="checkbox" checked={emailNow} onChange={(e) => setEmailNow(e.target.checked)} />
            {forLoan ? 'Email the statement to the borrower' : 'Email statements to borrowers after generating'}
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5 shrink-0">
          <Button color="gray" outline onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button color="blue" onClick={submit} loading={busy}>
            {forLoan ? 'Print' : 'Generate'}
          </Button>
        </div>
      </div>
    </div>
  )
}
